import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import PageHeader from '../components/PageHeader';
import Spinner from '../components/Spinner';
import { ENGINE_TYPES, formatSpecificationLabel, SPECIFICATION_DEFINITIONS, STATUSES } from '../features/planning/constants';
import { useProjects } from '../features/planning/hooks/useProjects';
import { useMachines } from '../features/planning/hooks/useMachines';
import { useSuppliers } from '../features/planning/hooks/useSuppliers';
import { useCategories } from '../features/planning/hooks/useCategories';
import { useOptions } from '../features/planning/hooks/useOptions';
import { useMachineTemplates } from '../features/planning/hooks/useMachineTemplates';
import { useProjectAddresses } from '../features/planning/hooks/useProjectAddresses';
import { useCategoryLinkedOptions } from '../features/planning/hooks/useCategoryLinkedOptions';
import { useSpecificationsForm } from '../features/planning/hooks/useSpecificationsForm';
import { useOrgSettings } from '../features/settings/hooks/useOrgSettings';
import SupplierModal from '../features/settings/components/suppliers/SupplierModal';
import CategoryModal from '../features/settings/components/categories/CategoryModal';
import OptionModal from '../features/settings/components/options/OptionModal';
import AddressModal from '../features/settings/components/projects/AddressModal';
import CategoryAutocompleteField from '../features/planning/components/CategoryAutocompleteField';
import OptionsAutocompleteField from '../features/planning/components/OptionsAutocompleteField';
import { useNotification } from '../context/useNotification';
import { formatAddressLine } from '../utils/formatAddress';
import { hasFullAccess } from '../utils/roles';
import { MACHINE_FORM_PAGE_LABELS as LABELS, PLANNING_LABELS } from '../utils/labels';
import type { EngineTypeKey, MachineInput, MachineOption, MachineTemplate, SpecificationKey, StatusKey } from '../features/planning/types';
import type { MemberRole } from '../types/roles';

const CREATE_SENTINEL = '__create__';

interface MachineFormPageProps {
  orgId: string | null;
  selectedProjectId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
}

interface MachineForm {
  name: string;
  category: string;
  category_id: string | null;
  machine_number: string;
  options: string[];
  option_ids: string[];
  supplier_id: string;
  address_id: string;
  status: StatusKey;
  engine_type: EngineTypeKey;
  pickup_on_end_date: boolean;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  notes: string;
}

type FormErrors = Partial<Record<keyof MachineForm, string>>;

export default function MachineFormPage({ orgId, selectedProjectId, isSuperAdmin, role }: MachineFormPageProps) {
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useNotification();

  const { projects, loading: projectsLoading } = useProjects(orgId);
  const validProjectId = projects.some((p) => p.id === selectedProjectId) ? selectedProjectId : null;
  const currentProjectId = validProjectId ?? projects[0]?.id ?? null;
  const currentProject = projects.find((p) => p.id === currentProjectId);

  const { addMachine } = useMachines(currentProjectId, orgId);
  const { suppliers, addSupplier } = useSuppliers(orgId);
  const { categories, addCategory } = useCategories(orgId);
  const { options, addOption } = useOptions(orgId);
  const { templates } = useMachineTemplates(orgId);
  const { addresses, addAddress } = useProjectAddresses(currentProjectId);
  const { settings } = useOrgSettings(orgId);

  const [quickCreate, setQuickCreate] = useState<'supplier' | 'category' | 'option' | 'address' | null>(null);

  const defaultAddressId = addresses.find((a) => a.is_default)?.id ?? addresses[0]?.id ?? '';
  const [form, setForm] = useState<MachineForm>({
    name: '',
    category: '',
    category_id: null,
    machine_number: '',
    options: [],
    option_ids: [],
    supplier_id: '',
    address_id: '',
    status: settings.machine_default_ordered ? 'ordered' : 'to_order',
    engine_type: 'none',
    pickup_on_end_date: settings.machine_end_date_definitive,
    start_date: '',
    start_time: settings.default_machine_start_time,
    end_date: '',
    end_time: settings.default_machine_end_time,
    notes: '',
  });
  // useOrgSettings starts from a hardcoded fallback and only resolves the org's real values
  // after its own async fetch completes — always *after* the `useState` above already ran, so
  // the settings-derived fields captured there are stale on every navigation to this page. Apply
  // the real values once they arrive, but only that once, so it can't clobber anything the user
  // already typed in the (rare) case they raced ahead of the fetch.
  const settingsAppliedRef = useRef(false);
  useEffect(() => {
    if (settingsAppliedRef.current || !settings.id) return;
    settingsAppliedRef.current = true;
    setForm((f) => ({
      ...f,
      status: settings.machine_default_ordered ? 'ordered' : 'to_order',
      pickup_on_end_date: settings.machine_end_date_definitive,
      start_time: settings.default_machine_start_time,
      end_time: settings.default_machine_end_time,
    }));
  }, [settings]);

  const [addressTouched, setAddressTouched] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const {
    categoryValue,
    categoryInputValue,
    filteredCatalogOptions,
    hasNoLinkedOptions,
    eligibleCategories,
    hasNoEligibleCategories,
    categorySpecs,
    sortedCategorySpecKeys,
  } = useCategoryLinkedOptions({
    categories,
    options,
    category: form.category,
    categoryId: form.category_id,
    selectedOptionIds: form.option_ids,
  });
  const specsForm = useSpecificationsForm();

  const selectedOptionsValue: (MachineOption | string)[] = [
    ...options.filter((o) => form.option_ids.includes(o.id)),
    ...form.options,
  ];

  const addressId = addressTouched ? form.address_id : (form.address_id || defaultAddressId);

  const handleTemplateSelect = (tpl: MachineTemplate | null) => {
    if (!tpl) return;
    const specs: Partial<Record<SpecificationKey, string>> = {};
    (tpl.specifications ?? []).forEach((s) => { specs[s.key] = String(s.value); });
    setForm((f) => ({
      ...f,
      category_id: tpl.category_id ?? f.category_id,
      category: tpl.category?.name ?? f.category,
      engine_type: tpl.engine_type ?? f.engine_type,
      supplier_id: tpl.supplier_id ?? f.supplier_id,
      option_ids: tpl.option_ids ?? [],
      options: [],
    }));
    specsForm.setSpecifications(specs);
    setErrors((e) => ({ ...e, category: undefined, supplier_id: undefined }));
  };

  const set = <K extends keyof MachineForm>(k: K, v: MachineForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const handleStartDateChange = (val: ReturnType<typeof dayjs> | null) => {
    const newDate = val ? val.format('YYYY-MM-DD') : '';
    setForm((f) => {
      const next = { ...f, start_date: newDate };
      if (newDate && next.end_date === newDate && next.end_time && next.end_time <= f.start_time) {
        next.end_time = '';
      }
      return next;
    });
    setErrors((e) => ({ ...e, start_date: undefined, end_date: undefined, end_time: undefined }));
  };

  const handleStartTimeChange = (val: ReturnType<typeof dayjs> | null) => {
    set('start_time', val ? val.format('HH:mm') : '');
  };

  const handleEndDateChange = (val: ReturnType<typeof dayjs> | null) => {
    const newDate = val ? val.format('YYYY-MM-DD') : '';
    setForm((f) => {
      const next = { ...f, end_date: newDate };
      if (
        newDate &&
        next.start_date === newDate &&
        next.start_time &&
        f.end_time &&
        next.start_time >= f.end_time
      ) {
        next.start_time = '';
      }
      return next;
    });
    setErrors((e) => ({ ...e, end_date: undefined, start_date: undefined, start_time: undefined }));
  };

  const handleEndTimeChange = (val: ReturnType<typeof dayjs> | null) => {
    set('end_time', val ? val.format('HH:mm') : '');
  };

  const toMinutes = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  };

  const isSameDay = form.start_date && form.start_date === form.end_date;

  const shouldDisableEndTime = (value: ReturnType<typeof dayjs>, view: 'hours' | 'minutes') => {
    if (!isSameDay || !form.start_time) return false;
    const start = toMinutes(form.start_time);
    if (view === 'hours') return value.hour() * 60 + 30 <= start;
    return value.hour() * 60 + value.minute() <= start;
  };

  const shouldDisableStartTime = (value: ReturnType<typeof dayjs>, view: 'hours' | 'minutes') => {
    if (!isSameDay || !form.end_time) return false;
    const end = toMinutes(form.end_time);
    if (view === 'hours') return value.hour() * 60 >= end;
    return value.hour() * 60 + value.minute() >= end;
  };

  const validate = (): FormErrors => {
    const e: FormErrors = {};
    if (!form.name.trim()) e.name = LABELS.nameRequired;
    if (!form.category.trim()) e.category = LABELS.categoryRequired;
    if (!form.supplier_id) e.supplier_id = LABELS.supplierRequired;
    if (!addressId) e.address_id = LABELS.addressRequired;
    if (!form.start_date) e.start_date = LABELS.startDateRequired;
    if (!form.end_date) e.end_date = LABELS.endDateRequired;
    if (form.start_date && form.end_date && form.end_date < form.start_date)
      e.end_date = LABELS.endDateAfterStart;
    if (
      form.start_date &&
      form.end_date &&
      form.end_date === form.start_date &&
      form.start_time &&
      form.end_time &&
      form.end_time <= form.start_time
    )
      e.end_time = LABELS.endTimeAfterStart;
    return e;
  };

  const handleCancel = () => navigate('/planning');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    const specsValid = specsForm.validateSpecifications(categorySpecs, sortedCategorySpecKeys, LABELS.specificationRequired);
    if (Object.keys(errs).length > 0 || !specsValid) {
      setErrors(errs);
      return;
    }
    const specificationsPayload = specsForm.buildSpecificationsPayload(sortedCategorySpecKeys);
    addMachine({ ...form, address_id: addressId, specifications: specificationsPayload } as MachineInput)
      .then(() => notifySuccess(PLANNING_LABELS.machineAddSuccess))
      .catch(() => notifyError(PLANNING_LABELS.mutationError));
    navigate('/planning');
  };

  if (projectsLoading && projects.length === 0) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <Spinner size="lg" className="text-primary" />
      </div>
    );
  }

  const archived = currentProject?.archived ?? false;
  const canEditMachines = !archived && hasFullAccess(role, isSuperAdmin);
  if (!canEditMachines) return <Navigate to="/planning" replace />;

  return (
    <div className="h-dvh flex flex-col bg-background">
      <PageHeader />
      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleCancel}
              data-testid="back-button"
            >
              {LABELS.backToPlanning}
            </Button>
            <h1 className="text-xl font-medium mt-2">{LABELS.pageTitle}</h1>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-4">
              <section className="rounded-2xl border border-border bg-surface p-4 md:p-6">
                <h2 className="text-sm font-medium text-text-secondary mt-0 mb-3">{LABELS.sectionMachineHeading}</h2>
                <div className="flex flex-col gap-2.5">
                  {templates.length > 0 && (
                    <Autocomplete
                      options={templates}
                      getOptionLabel={(t) => t.name}
                      onChange={(_, tpl) => handleTemplateSelect(tpl)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={LABELS.templateLabel}
                          placeholder={LABELS.templatePlaceholder}
                          slotProps={{
                            ...params.slotProps,
                            htmlInput: { ...params.slotProps.htmlInput, 'aria-label': LABELS.templateLabel },
                          }}
                          fullWidth
                        />
                      )}
                    />
                  )}

                  <TextField
                    label={LABELS.nameLabel}
                    required
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    fullWidth
                  />

                  <CategoryAutocompleteField
                    categories={eligibleCategories}
                    value={categoryValue}
                    inputValue={categoryInputValue}
                    hasNoEligibleCategories={hasNoEligibleCategories}
                    label={LABELS.categoryLabel}
                    addCategoryLabel={LABELS.addCategoryOption}
                    noEligibleCategoriesLabel={LABELS.noLinkedCategories}
                    required
                    error={!!errors.category}
                    helperText={errors.category}
                    onSelectCategory={(cat) => {
                      setForm((f) => ({ ...f, category: cat.name, category_id: cat.id }));
                      specsForm.clearStaleSpecifications((cat.specifications ?? []).map((s) => s.key));
                      setErrors((e) => ({ ...e, category: undefined }));
                    }}
                    onFreeTextChange={(text) => {
                      setForm((f) => ({ ...f, category: text, category_id: null }));
                      specsForm.clearStaleSpecifications([]);
                      setErrors((e) => ({ ...e, category: undefined }));
                    }}
                    onCreateNew={() => setQuickCreate('category')}
                  />

                  <TextField
                    label={LABELS.machineNumberLabel}
                    value={form.machine_number}
                    onChange={(e) => set('machine_number', e.target.value)}
                    fullWidth
                  />

                  <FormControl fullWidth>
                    <InputLabel>{LABELS.engineTypeLabel}</InputLabel>
                    <Select
                      label={LABELS.engineTypeLabel}
                      value={form.engine_type}
                      onChange={(e) => set('engine_type', e.target.value as EngineTypeKey)}
                      inputProps={{ 'aria-label': LABELS.engineTypeLabel }}
                    >
                      {Object.entries(ENGINE_TYPES).map(([k, v]) => (
                        <MenuItem key={k} value={k} data-testid={`option-engine-${k}`}>
                          {v.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {sortedCategorySpecKeys.map((k) => {
                    const spec = categorySpecs.find((s) => s.key === k);
                    return (
                      <TextField
                        key={k}
                        type="number"
                        label={formatSpecificationLabel(k)}
                        required={spec?.required}
                        value={specsForm.specifications[k] ?? ''}
                        onChange={(e) => specsForm.setSpecificationValue(k, e.target.value)}
                        error={!!specsForm.specErrors[k]}
                        helperText={specsForm.specErrors[k]}
                        fullWidth
                        slotProps={{
                          htmlInput: { step: 10 ** -SPECIFICATION_DEFINITIONS[k].decimals, 'data-testid': `spec-input-${k}` },
                        }}
                      />
                    );
                  })}

                  <OptionsAutocompleteField
                    options={filteredCatalogOptions}
                    value={selectedOptionsValue}
                    hasNoLinkedOptions={hasNoLinkedOptions}
                    label={LABELS.optionsLabel}
                    addOptionLabel={LABELS.addOptionOption}
                    noLinkedOptionsLabel={LABELS.noLinkedOptions}
                    onChange={(optionIds, freeTextOptions) =>
                      setForm((f) => ({ ...f, option_ids: optionIds, options: freeTextOptions }))
                    }
                    onCreateNew={() => setQuickCreate('option')}
                  />

                  <FormControl fullWidth error={!!errors.supplier_id}>
                    <InputLabel required>{LABELS.supplierLabel}</InputLabel>
                    <Select
                      label={LABELS.supplierLabel}
                      value={form.supplier_id}
                      onChange={(e) => {
                        if (e.target.value === CREATE_SENTINEL) {
                          setQuickCreate('supplier');
                          return;
                        }
                        set('supplier_id', e.target.value);
                      }}
                      inputProps={{ 'aria-label': LABELS.supplierLabel }}
                    >
                      <MenuItem value={CREATE_SENTINEL} data-testid="option-add-supplier" className="text-primary">
                        <AddIcon fontSize="small" className="mr-1" />
                        {LABELS.addSupplierOption}
                      </MenuItem>
                      {suppliers.map((s) => (
                        <MenuItem key={s.id} value={s.id} data-testid={`option-${s.id}`}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.supplier_id && <FormHelperText>{errors.supplier_id}</FormHelperText>}
                  </FormControl>
                </div>
              </section>

              <section className="rounded-2xl border border-border bg-surface p-4 md:p-6">
                <h2 className="text-sm font-medium text-text-secondary mt-0 mb-3">{LABELS.sectionLocationHeading}</h2>
                <FormControl fullWidth error={!!errors.address_id}>
                  <InputLabel required>{LABELS.addressLabel}</InputLabel>
                  <Select
                    label={LABELS.addressLabel}
                    value={addressId}
                    onChange={(e) => {
                      if (e.target.value === CREATE_SENTINEL) {
                        setQuickCreate('address');
                        return;
                      }
                      setAddressTouched(true);
                      set('address_id', e.target.value);
                    }}
                    inputProps={{ 'aria-label': LABELS.addressLabel }}
                    renderValue={(v) => addresses.find((a) => a.id === v)?.name}
                  >
                    <MenuItem value={CREATE_SENTINEL} data-testid="option-add-address" className="text-primary">
                      <AddIcon fontSize="small" className="mr-1" />
                      {LABELS.addAddressOption}
                    </MenuItem>
                    {addresses.map((a) => (
                      <MenuItem key={a.id} value={a.id} data-testid={`option-${a.id}`}>
                        <div className="flex flex-col">
                          <span>{a.name}</span>
                          <span className="text-xs text-text-secondary">{formatAddressLine(a)}</span>
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.address_id && <FormHelperText>{errors.address_id}</FormHelperText>}
                  {addresses.length === 0 && <FormHelperText>{LABELS.noAddresses}</FormHelperText>}
                </FormControl>
              </section>

              <section className="rounded-2xl border border-border bg-surface p-4 md:p-6">
                <h2 className="text-sm font-medium text-text-secondary mt-0 mb-3">{LABELS.sectionPlanningHeading}</h2>
                <div className="flex flex-col gap-2.5">
                  <FormControl fullWidth>
                    <InputLabel>{LABELS.statusLabel}</InputLabel>
                    <Select
                      label={LABELS.statusLabel}
                      value={form.status}
                      onChange={(e) => set('status', e.target.value as StatusKey)}
                      inputProps={{ 'aria-label': LABELS.statusLabel }}
                    >
                      {Object.entries(STATUSES).map(([k, v]) => (
                        <MenuItem key={k} value={k} data-testid={`option-status-${k}`}>
                          {v.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <div className="grid grid-cols-2 gap-3">
                    <DatePicker
                      label={LABELS.startDateLabel}
                      value={form.start_date ? dayjs(form.start_date) : null}
                      onChange={handleStartDateChange}
                      maxDate={form.end_date ? dayjs(form.end_date) : undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!errors.start_date,
                          helperText: errors.start_date,
                        },
                      }}
                    />
                    <TimePicker
                      label={LABELS.startTimeLabel}
                      ampm={false}
                      minutesStep={30}
                      timeSteps={{ minutes: 30 }}
                      value={form.start_time ? dayjs(`2000-01-01T${form.start_time}`) : null}
                      onChange={handleStartTimeChange}
                      shouldDisableTime={shouldDisableStartTime}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <DatePicker
                      label={LABELS.endDateLabel}
                      value={form.end_date ? dayjs(form.end_date) : null}
                      onChange={handleEndDateChange}
                      minDate={form.start_date ? dayjs(form.start_date) : undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!errors.end_date,
                          helperText: errors.end_date,
                        },
                      }}
                    />
                    <TimePicker
                      label={LABELS.endTimeLabel}
                      ampm={false}
                      minutesStep={30}
                      timeSteps={{ minutes: 30 }}
                      value={form.end_time ? dayjs(`2000-01-01T${form.end_time}`) : null}
                      onChange={handleEndTimeChange}
                      shouldDisableTime={shouldDisableEndTime}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.end_time,
                          helperText: errors.end_time,
                        },
                      }}
                    />
                  </div>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.pickup_on_end_date}
                        onChange={(e) => set('pickup_on_end_date', e.target.checked)}
                      />
                    }
                    label={LABELS.pickupOnEndDate}
                  />

                  <TextField
                    label={LABELS.notesLabel}
                    multiline
                    rows={2}
                    value={form.notes}
                    onChange={(e) => set('notes', e.target.value)}
                    fullWidth
                  />
                </div>
              </section>

              <div className="flex justify-end gap-2">
                <Button type="button" onClick={handleCancel} variant="outlined" color="inherit" data-testid="cancel-button">
                  {LABELS.cancelButton}
                </Button>
                <Button type="submit" variant="contained" data-testid="save-button">
                  {LABELS.saveButton}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </main>

      {quickCreate === 'supplier' && (
        <SupplierModal
          onClose={() => setQuickCreate(null)}
          onSave={async (name, email) => {
            const created = await addSupplier(name, email);
            if (!created) return false;
            setForm((f) => ({ ...f, supplier_id: created.id }));
            setErrors((e) => ({ ...e, supplier_id: undefined }));
            return true;
          }}
        />
      )}
      {quickCreate === 'category' && (
        <CategoryModal
          options={options}
          existingCategories={categories}
          initialOptionIds={form.option_ids}
          onClose={() => setQuickCreate(null)}
          onSave={async (name, optionIds, specifications) => {
            const created = await addCategory(name, optionIds, specifications);
            if (!created) return false;
            setForm((f) => ({ ...f, category: created.name, category_id: created.id }));
            setErrors((e) => ({ ...e, category: undefined }));
            return true;
          }}
          onAddOption={async (name, categoryIds) => addOption(name, categoryIds)}
        />
      )}
      {quickCreate === 'option' && (
        <OptionModal
          categories={categories}
          existingOptions={options}
          initialCategoryIds={form.category_id ? [form.category_id] : []}
          onClose={() => setQuickCreate(null)}
          onSave={async (name, categoryIds) => {
            const created = await addOption(name, categoryIds);
            if (!created) return false;
            setForm((f) => ({ ...f, option_ids: [...f.option_ids, created.id] }));
            return true;
          }}
        />
      )}
      {quickCreate === 'address' && (
        <AddressModal
          onClose={() => setQuickCreate(null)}
          onSave={async (name, street, houseNumber, postalCode, city) => {
            const created = await addAddress(name, street, houseNumber, postalCode, city);
            if (!created) return false;
            setAddressTouched(true);
            setForm((f) => ({ ...f, address_id: created.id }));
            setErrors((e) => ({ ...e, address_id: undefined }));
            return true;
          }}
        />
      )}
    </div>
  );
}
